import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../layout/header/Header';
import Footer from '../../layout/footer/Footer';
import { GlobalContext } from '../../store/GlobalContext';
import api from '../../api/axios';
import {
  GuestCartItem,
  clearGuestCart,
  getGuestCart,
  getGuestCartTotal,
  removeFromGuestCart,
  updateGuestCartItem,
} from '../../utils/guestCart';
import { getFallbackProductImage, resolveProductImage } from '../../data/productsApi';
import './Checkout.css';

type ServerCartItem = {
  productId: string;
  name: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  stock: number;
  image?: string;
};

type OrderForm = {
  name: string;
  email: string;
  phoneNumber: string;
  address: string;
  notes: string;
  discountCode: string;
};

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { state, setCartCount } = useContext(GlobalContext);
  const token = state.auth.token;
  const user = state.auth.user;
  const isLoggedIn = Boolean(token);

  const [serverItems, setServerItems] = useState<ServerCartItem[]>([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [loadingCart, setLoadingCart] = useState(false);
  const [guestItems, setGuestItems] = useState<GuestCartItem[]>([]);

  const [form, setForm] = useState<OrderForm>({
    name: isLoggedIn
      ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || String(user?.username || '')
      : '',
    email: isLoggedIn ? String(user?.email || '') : '',
    phoneNumber: '',
    address: '',
    notes: '',
    discountCode: '',
  });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState<{ orderNumber: string } | null>(null);

  const loadServerCart = useCallback(async () => {
    if (!token) return;
    setLoadingCart(true);
    try {
      const res = await api.get('/cart/my', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items: ServerCartItem[] = Array.isArray(res.data?.items) ? res.data.items : [];
      setServerItems(items);
      setServerTotal(Number(res.data?.totalAmount ?? 0));
      setCartCount(items.reduce((s, i) => s + i.quantity, 0));
    } catch {
      setServerItems([]);
      setServerTotal(0);
    } finally {
      setLoadingCart(false);
    }
  }, [token, setCartCount]);

  useEffect(() => {
    if (isLoggedIn) {
      loadServerCart();
    } else {
      const cart = getGuestCart();
      setGuestItems(cart);
      setCartCount(cart.reduce((s, i) => s + i.quantity, 0));
    }
  }, [isLoggedIn, loadServerCart, setCartCount]);

  const updateServerQty = async (productId: string, quantity: number) => {
    try {
      const res = await api.put(
        `/cart/my/items/${productId}`,
        { quantity },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const items: ServerCartItem[] = Array.isArray(res.data?.items) ? res.data.items : [];
      setServerItems(items);
      setServerTotal(Number(res.data?.totalAmount ?? 0));
      setCartCount(items.reduce((s, i) => s + i.quantity, 0));
    } catch {}
  };

  const removeServerItem = async (productId: string) => {
    try {
      const res = await api.delete(`/cart/my/items/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items: ServerCartItem[] = Array.isArray(res.data?.items) ? res.data.items : [];
      setServerItems(items);
      setServerTotal(Number(res.data?.totalAmount ?? 0));
      setCartCount(items.reduce((s, i) => s + i.quantity, 0));
    } catch {}
  };

  const clearServerCart = async () => {
    try {
      await api.delete('/cart/my/clear', { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
    setServerItems([]);
    setServerTotal(0);
    setCartCount(0);
  };

  const updateGuestQty = (productId: string, qty: number) => {
    const updated = updateGuestCartItem(productId, qty);
    setGuestItems(updated);
    setCartCount(updated.reduce((s, i) => s + i.quantity, 0));
  };

  const removeGuest = (productId: string) => {
    const updated = removeFromGuestCart(productId);
    setGuestItems(updated);
    setCartCount(updated.reduce((s, i) => s + i.quantity, 0));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = (): string => {
    const items = isLoggedIn ? serverItems : guestItems;
    if (items.length === 0) return 'Your cart is empty.';
    if (!form.name.trim()) return 'Full name is required.';
    const phone = form.phoneNumber.trim();
    if (!phone) return 'Phone number is required.';
    if (!/^(\+923[0-9]{9}|923[0-9]{9}|03[0-9]{9}|3[0-9]{9})$/.test(phone)) {
      return 'Phone must be in 03xxxxxxxxx or +923xxxxxxxxx format.';
    }
    if (!form.address.trim()) return 'Delivery address is required.';
    return '';
  };

  const buildOrderPayload = (
    items: Array<{ productId: string; qty: number; price: number }>,
    total: number,
  ) => ({
    name: form.name.trim(),
    email: form.email.trim() || undefined,
    phoneNumber: form.phoneNumber.trim(),
    address: form.address.trim(),
    items: items.map((i) => ({ productId: i.productId, quantity: i.qty, price: i.price })),
    totalAmount: total,
    status: 'pending',
    paymentStatus: 'unpaid',
    paymentMethod: 'Cash on Delivery' as const,
    notes: form.notes.trim() || undefined,
    discountCode: form.discountCode.trim() || undefined,
  });

  const submitOrder = async () => {
    setSubmitting(true);
    try {
      if (isLoggedIn) {
        const res = await api.post(
          '/orders',
          buildOrderPayload(serverItems.map((i) => ({ productId: i.productId, qty: i.quantity, price: i.unitPrice })), serverTotal),
          { headers: { Authorization: `Bearer ${token}` } },
        );
        await clearServerCart();
        setOrderSuccess({ orderNumber: res.data?.orderNumber ?? '' });
      } else {
        const total = getGuestCartTotal();
        const res = await api.post(
          '/orders/guest',
          buildOrderPayload(guestItems.map((i) => ({ productId: i.productId, qty: i.quantity, price: i.price })), total),
        );
        clearGuestCart();
        setGuestItems([]);
        setCartCount(0);
        setOrderSuccess({ orderNumber: res.data?.orderNumber ?? '' });
      }
    } catch (err: any) {
      setFormError(err?.response?.data?.message || 'Failed to place order. Please try again.');
    } finally {
      setSubmitting(false);
      setShowConfirmModal(false);
    }
  };

  const handlePlaceOrderClick = () => {
    setFormError('');
    const err = validate();
    if (err) {
      setShowConfirmModal(false);
      setFormError(err);
      return;
    }

    setShowConfirmModal(true);
  };

  const cartItems = isLoggedIn ? serverItems : guestItems;
  const cartTotal = isLoggedIn ? serverTotal : getGuestCartTotal();
  const isEmpty = cartItems.length === 0;
  const normalizedPhone = form.phoneNumber.trim();
  const normalizedAddress = form.address.trim();
  const normalizedName = form.name.trim();
  const normalizedEmail = form.email.trim();

  const renderCartItem = (item: ServerCartItem | GuestCartItem) => {
    const id = item.productId;
    const qty = item.quantity;
    const unitPrice = 'unitPrice' in item ? item.unitPrice : item.price;
    const maxQty = 'stock' in item ? item.stock : 99;
    const img = 'image' in item && item.image ? item.image : undefined;
    const imageSrc = img ? resolveProductImage(img) : getFallbackProductImage();
    return (
      <div className="checkout-cart-item" key={id}>
        <img
          className="checkout-item-img"
          src={imageSrc}
          alt={item.name}
          onError={(event) => {
            event.currentTarget.src = getFallbackProductImage();
          }}
        />
        <div className="checkout-item-info">
          <div className="checkout-item-name">{item.name}</div>
          <div className="checkout-item-price">
            Rs {unitPrice.toFixed(0)} x {qty} = <strong>Rs {(unitPrice * qty).toFixed(0)}</strong>
          </div>
          <div className="checkout-qty-ctrl">
            <button className="checkout-qty-btn" disabled={qty <= 1}
              onClick={() => isLoggedIn ? updateServerQty(id, qty - 1) : updateGuestQty(id, qty - 1)}>-</button>
            <span className="checkout-qty-num">{qty}</span>
            <button className="checkout-qty-btn" disabled={qty >= maxQty}
              onClick={() => isLoggedIn ? updateServerQty(id, qty + 1) : updateGuestQty(id, qty + 1)}>+</button>
          </div>
        </div>
        <button className="checkout-remove-btn"
          onClick={() => isLoggedIn ? removeServerItem(id) : removeGuest(id)}>Remove</button>
      </div>
    );
  };

  if (orderSuccess) {
    return (
      <>
        <Header />
        <div className="checkout-page">
          <div className="container" style={{ maxWidth: 600 }}>
            <div className="checkout-success">
              <div className="checkout-success-icon">&#10003;</div>
              <h4>Order Placed!</h4>
              {orderSuccess.orderNumber && (
                <p>Order number: <strong>{orderSuccess.orderNumber}</strong></p>
              )}
              <p>We will prepare your order and contact you on your phone to confirm delivery.</p>
              {isLoggedIn && (
                <button className="checkout-place-btn" style={{ maxWidth: 240, margin: '1rem auto 0', display: 'block' }}
                  onClick={() => navigate('/account/orders')}>View My Orders</button>
              )}
              <button className="checkout-place-btn" style={{ maxWidth: 240, margin: '0.7rem auto 0', background: '#555', display: 'block' }}
                onClick={() => navigate('/products')}>Continue Shopping</button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginBottom: '0.75rem' }}>Please confirm your order details before placing it.</p>

          <div className="checkout-confirm-block">
            <div className="checkout-confirm-row"><span>Name</span><strong>{normalizedName || '-'}</strong></div>
            <div className="checkout-confirm-row"><span>Phone</span><strong>{normalizedPhone || '-'}</strong></div>
            <div className="checkout-confirm-row"><span>Email</span><strong>{normalizedEmail || 'Not provided'}</strong></div>
            <div className="checkout-confirm-row checkout-confirm-row-address"><span>Address</span><strong>{normalizedAddress || '-'}</strong></div>
            <div className="checkout-confirm-row"><span>Payment</span><strong>Cash on Delivery</strong></div>
          </div>

          <div className="checkout-confirm-items">
            {cartItems.map((item) => {
              const price = 'unitPrice' in item ? item.unitPrice : item.price;
              return (
                <div className="checkout-confirm-row" key={item.productId}>
                  <span>{item.name} x{item.quantity}</span>
                  <strong>Rs {(price * item.quantity).toFixed(0)}</strong>
                </div>
              );
            })}
            <div className="checkout-confirm-row checkout-confirm-total">
              <span>Total</span>
              <strong>Rs {cartTotal.toFixed(0)}</strong>
            </div>
          </div>

          {form.notes.trim() ? (
            <div className="checkout-confirm-note">
              <span>Order note:</span>
              <strong>{form.notes.trim()}</strong>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button className="checkout-confirm-cancel" onClick={() => setShowConfirmModal(false)} disabled={submitting}>Cancel</button>
          <button className="checkout-confirm-submit" onClick={submitOrder} disabled={submitting}>
            {submitting ? 'Placing Order...' : 'Confirm & Place Order'}
          </button>
        </Modal.Footer>
      </Modal>

      <div className="checkout-page">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="checkout-title">Checkout</h2>
            <Link to="/products" style={{ fontSize: '0.85rem', color: '#555' }}>Back to shopping</Link>
          </div>

          {!isLoggedIn && (
            <div className="checkout-guest-banner">
              Guest checkout - your order will be saved with your phone number.{' '}
              <Link to="/login" state={{ from: '/checkout' }}>Sign in</Link> for full order history and tracking.
            </div>
          )}

          <div className="row">
            <div className="col-lg-8">
              <div className="checkout-section">
                <h5>Your Cart</h5>
                {loadingCart && <p style={{ fontSize: '0.9rem', color: '#777' }}>Loading cart...</p>}
                {!loadingCart && isEmpty && (
                  <p style={{ fontSize: '0.9rem', color: '#777' }}>
                    Your cart is empty. <Link to="/products">Browse products</Link>
                  </p>
                )}
                {!loadingCart && cartItems.map(renderCartItem)}
              </div>

              <div className="checkout-section">
                <h5>Delivery Details</h5>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label" style={{ fontSize: '0.82rem', fontWeight: 600 }}>Full Name *</label>
                    <input className="form-control" name="name" value={form.name} onChange={handleChange} placeholder="Your full name" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label" style={{ fontSize: '0.82rem', fontWeight: 600 }}>Email (optional)</label>
                    <input className="form-control" name="email" type="email" value={form.email} onChange={handleChange} placeholder="For order confirmation" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label" style={{ fontSize: '0.82rem', fontWeight: 600 }}>Phone Number *</label>
                    <input className="form-control" name="phoneNumber" value={form.phoneNumber} onChange={handleChange} placeholder="03xxxxxxxxx or +923xxxxxxxxx" />
                    <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 3 }}>We will call you on this number for delivery</div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label" style={{ fontSize: '0.82rem', fontWeight: 600 }}>Discount Code</label>
                    <input className="form-control" name="discountCode" value={form.discountCode} onChange={handleChange} placeholder="Enter code if you have one" />
                  </div>
                  <div className="col-12">
                    <label className="form-label" style={{ fontSize: '0.82rem', fontWeight: 600 }}>Delivery Address *</label>
                    <input className="form-control" name="address" value={form.address} onChange={handleChange} placeholder="Full address including city" />
                  </div>
                  <div className="col-12">
                    <label className="form-label" style={{ fontSize: '0.82rem', fontWeight: 600 }}>Order Notes (optional)</label>
                    <textarea className="form-control" name="notes" value={form.notes} onChange={handleChange} rows={2} placeholder="Any special instructions" />
                  </div>
                </div>
                {formError && <div className="alert alert-danger mt-3" style={{ fontSize: '0.85rem' }}>{formError}</div>}
              </div>
            </div>

            <div className="col-lg-4">
              <div className="checkout-summary">
                <h5>Order Summary</h5>
                {cartItems.map((item) => {
                  const price = 'unitPrice' in item ? item.unitPrice : item.price;
                  return (
                    <div className="checkout-summary-row" key={item.productId}>
                      <span style={{ maxWidth: '65%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name} x{item.quantity}</span>
                      <span>Rs {(price * item.quantity).toFixed(0)}</span>
                    </div>
                  );
                })}
                <div className="checkout-summary-row" style={{ marginTop: '0.5rem' }}>
                  <span>Delivery</span><span>Free</span>
                </div>
                <div className="checkout-summary-total">
                  <span>Total</span><span>Rs {cartTotal.toFixed(0)}</span>
                </div>
                <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '0.6rem' }}>
                  Payment: Cash on Delivery
                </div>
                <button className="checkout-place-btn" onClick={handlePlaceOrderClick} disabled={submitting || isEmpty || loadingCart}>
                  {submitting ? 'Placing Order...' : 'Place Order'}
                </button>
                {!isLoggedIn && (
                  <div style={{ textAlign: 'center', fontSize: '0.78rem', color: '#888', marginTop: '0.7rem' }}>
                    <Link to="/login" state={{ from: '/checkout' }}>Sign in</Link> to save your order history
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Checkout;
